import {
  createBigDecimal,
  equals,
  greaterThan,
  minus,
  plus,
} from '@0xtorch/big-decimal'
import type { Action, Transfer } from '@0xtorch/core'
import { createSolanaAddressId } from '@pkg/solana'
import { fromIsRelated, isFeePayer, isRelatedTransfer } from '../../conditions'
import { haveTokenTransfers } from '../../conditions/haveTokenTransfers'
import { nativeAsset, nativeTokenDecimals } from '../../constants'
import { createAction } from '../../generators/createAction'
import { createTokenTransfer } from '../../generators/createTokenTransfer'
import type { SolanaConverter } from '../../type'

/**
 * Jupiter Swap 処理
 * Jupiterプログラムを使用したトークンスワップを処理する
 *
 * **生成する Action**
 * 'trade' タイプのActionを生成し、スワップ処理を記録する
 *
 * **条件 (一致すべき pattern)**
 * - fromIsRelated: トランザクション送信者が関連アドレスであること
 * - haveTokenTransfers: トークン転送が含まれていること
 *
 * **判定理由**
 * JupiterプログラムIDが含まれ、トークン転送を伴うトランザクションはスワップと判定
 *
 * **処理内容**
 * tokenTransfersFromInstructionsから関連アドレスに関係する転送を抽出し、
 * trade Actionのtransfersに追加する
 *
 * SOLを受け取るスワップの場合、wSOLアカウントを経由するため
 * tokenTransfersFromInstructionsではSOL入金を検出できない場合がある
 * そのため、solTransfersからユーザーへのSOL入金を抽出して補完する
 */
const convertToActions: SolanaConverter = (args) => {
  const { transaction, source, relatedAddresses } = args

  // 送信者が関連アドレスかチェック
  if (!fromIsRelated(args)) return undefined

  // トークン転送が含まれているかチェック
  if (!haveTokenTransfers(transaction)) return undefined

  // tokenTransfersFromInstructionsから関連アドレスの転送を抽出
  const actions: Action[] = []

  const accountTransfers: Transfer[] = []
  const swapTransfers: Transfer[] = []
  if (transaction.tokenTransfersFromInstructions) {
    for (const tokenTransfer of transaction.tokenTransfersFromInstructions) {
      // 関連アドレスが送信元または送信先の場合のみ処理
      if (!isRelatedTransfer(tokenTransfer, relatedAddresses)) {
        continue
      }

      const transfer = createTokenTransfer({
        tokenTransfer,
        relatedAddresses,
      })

      if (transfer) {
        if (
          tokenTransfer.instructionType === 'createAccount' ||
          tokenTransfer.instructionType === 'closeAccount'
        ) {
          accountTransfers.push(transfer)
        } else {
          swapTransfers.push(transfer)
        }
      }
    }
  }

  // solTransfersからユーザーへのSOL入金を抽出（wSOL経由のスワップ結果を補完）
  // tokenTransfersFromInstructionsではwSOLアカウント経由の入金を検出できない場合があるため
  if (transaction.solTransfers) {
    for (const solTransfer of transaction.solTransfers) {
      // toがユーザーアドレスで、fromがない（または非関連アドレス）の場合
      // これはスワップでユーザーが受け取ったSOLを示す
      if (solTransfer.to) {
        const toId = createSolanaAddressId({ address: solTransfer.to })
        const fromId = solTransfer.from
          ? createSolanaAddressId({ address: solTransfer.from })
          : undefined

        // toが関連アドレスで、fromが非関連（またはundefined）の場合はSOL入金
        if (
          relatedAddresses.has(toId) &&
          (!fromId || !relatedAddresses.has(fromId))
        ) {
          swapTransfers.push({
            direction: 'in',
            asset: nativeAsset,
            amount: createBigDecimal(solTransfer.amount, nativeTokenDecimals),
          })
        }
      }
    }
  }

  // trade Actionを作成
  if (swapTransfers.length >= 2) {
    const action = createAction({
      order: actions.length,
      type: 'trade',
      transfers: swapTransfers,
      transaction,
      source,
    })

    actions.push(action)
  }

  if (accountTransfers.length >= 1) {
    // amount の計算
    const amount = accountTransfers.reduce((acc, transfer) => {
      return transfer.direction === 'in'
        ? plus(acc, transfer.amount)
        : minus(acc, transfer.amount)
    }, createBigDecimal(0))

    // 0 以上になることはない
    if (!equals(createBigDecimal(0), amount)) {
      const action = createAction({
        order: actions.length,
        type: 'fee',
        transfers: [
          {
            direction: 'out',
            asset: nativeAsset,
            amount,
          },
        ],
        transaction,
        source,
      })
      actions.push(action)
    } else if (greaterThan(amount, createBigDecimal(0))) {
      throw new Error('amount is less than 0')
    }
  }

  // トランザクション手数料をout transferとして追加
  if (isFeePayer(args) && transaction.meta?.fee) {
    const feeAction = createAction({
      order: actions.length,
      type: 'transaction-fee',
      transfers: [
        {
          direction: 'out',
          asset: nativeAsset,
          amount: createBigDecimal(
            BigInt(transaction.meta.fee),
            nativeTokenDecimals,
          ),
        },
      ],
      transaction,
      source,
    })

    actions.push(feeAction)
  }

  return actions
}

export default convertToActions
